import { type CustomField } from '../../../types/custom-fields.type';
import { InlineTextInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-text-input';
import { InlineNumberInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-number-input';
import { InlineBooleanInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-boolean-input';
import { InlineDateInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-date-input';
import { InlineSelectInput } from 'src/modules/shared/components/molecules/inputs/inline/inline-select-input';
import { type CustomFieldEntityType, CustomFieldType } from 'src/api/generated';
import { CustomFieldInlineFileInput } from './custom-field-inline-file-input';
import type {
  ComponentAppearance,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';
import {
  formatCustomFieldValue,
  parseCustomFieldValue,
} from 'src/modules/shared/utils/custom-fields.util';

interface CustomFieldEditorProps {
  field: CustomField;
  onSave: (value: unknown) => Promise<void>;
  disabled?: boolean;
  loading?: boolean;
  readOnly?: boolean;
  entityType?: CustomFieldEntityType;
  entityId?: number;
  size?: ComponentSize;
  variant?: ComponentVariant;
  appearance?: ComponentAppearance;
  layout?: 'inline' | 'stacked';
}

export const CustomFieldEditor: React.FC<CustomFieldEditorProps> = ({
  field,
  onSave,
  disabled = false,
  readOnly = false,
  loading = false,
  entityType,
  entityId,
  size = 'sm',
  variant = 'primary',
  appearance = 'outline',
  layout = 'inline',
}) => {
  const handleSave = async (value: string) => {
    const parsedValue = parseCustomFieldValue(value, field.type);
    await onSave(parsedValue);
  };

  const stringValue = formatCustomFieldValue(field.value, field.type);
  const isDisabled = disabled || readOnly;

  const options =
    field.options?.map((option) => ({
      label: option.label,
      value: option.value,
    })) ?? [];

  switch (field.type) {
    case CustomFieldType.NUMBER:
      return (
        <InlineNumberInput
          label={field.name}
          value={stringValue}
          onSave={handleSave}
          disabled={isDisabled}
          loading={loading}
          size={size}
          variant={variant}
          appearance={appearance}
          layout={layout}
        />
      );
    case CustomFieldType.BOOLEAN:
      return (
        <InlineBooleanInput
          label={field.name}
          value={stringValue}
          onSave={handleSave}
          disabled={isDisabled}
          loading={loading}
          size={size}
          variant={variant}
          layout={layout}
        />
      );
    case CustomFieldType.DATE:
      return (
        <InlineDateInput
          label={field.name}
          value={stringValue}
          onSave={handleSave}
          type={field.includeTime ? 'datetime-local' : 'date'}
          disabled={isDisabled}
          loading={loading}
          size={size}
          variant={variant}
          appearance={appearance}
          layout={layout}
        />
      );
    case CustomFieldType.LIST:
      return (
        <InlineSelectInput
          label={field.name}
          value={stringValue}
          options={options}
          onSave={handleSave}
          disabled={isDisabled}
          loading={loading}
          size={size}
          variant={variant}
          appearance={appearance}
          layout={layout}
        />
      );
    case CustomFieldType.FILE:
      if (!entityType || !entityId) return null;

      return (
        <CustomFieldInlineFileInput
          label={field.name}
          value={stringValue}
          onSave={handleSave}
          disabled={isDisabled}
          loading={loading}
          entityType={entityType}
          entityId={entityId}
          size={size}
          variant={variant}
          layout={layout}
        />
      );
    case CustomFieldType.STRING:
    default:
      return (
        <InlineTextInput
          label={field.name}
          value={stringValue}
          onSave={handleSave}
          disabled={isDisabled}
          loading={loading}
          size={size}
          variant={variant}
          appearance={appearance}
          layout={layout}
        />
      );
  }
};
